import { Metadata } from 'next';

export interface PageMetadata {
  title: string;
  description: string;
  tourImage?: string;
  website: string;
  slug: string;
  type: string;
  fbAppId?: string;
  faviconUrl?: string;
}

export interface TourData {
  title: string;
  description: string;
  tourImage?: string;
  website: string;
  faviconUrl?: string;
  fbAppId?: string;
  charset?: string;
}

export function getPageMetadata({
  tourData,
  slug,
  type,
}: {
  tourData: TourData;
  slug: string;
  type: string;
}): Metadata {
  const {
    title,
    description,
    tourImage,
    website,
    faviconUrl,
    fbAppId = '741048509750318',
  } = tourData;

  const pageUrl = `https://${website}/${type}/${slug}`;

  return {
    title,
    description,
    openGraph: {
      title,
      description,
      type: 'website',
      url: pageUrl,
      siteName: website,
      locale: 'en_US',
      ...(tourImage && {
        images: [
          {
            url: tourImage,
            width: 1200,
            height: 630,
            alt: title,
          },
        ],
      }),
    },
    twitter: {
      card: 'summary_large_image',
      title,
      description,
      ...(tourImage && {
        images: [
          {
            url: tourImage,
            alt: title,
          },
        ],
      }),
    },
    other: {
      'fb:app_id': fbAppId,
    },
    ...(faviconUrl && {
      icons: [
        {
          rel: 'icon',
          url: faviconUrl,
        },
      ],
    }),
  };
}

export function generateMetaTags(metadata: PageMetadata) {
  const {
    title,
    description,
    tourImage,
    website,
    slug,
    type,
    fbAppId = '741048509750318',
    faviconUrl,
  } = metadata;

  const pageUrl = `https://${website}/${type}/${slug}`;

  return {
    // Basic meta tags
    title,
    description,
    pageUrl,
    
    // Open Graph tags
    ogTags: {
      'og:title': title,
      'og:description': description,
      'og:type': 'website',
      'og:url': pageUrl,
      'og:site_name': 'Treedis',
      'og:locale': 'en_US',
      ...(tourImage && {
        'og:image': tourImage,
        'og:image:secure_url': tourImage,
        'og:image:type': 'image/jpeg',
        'og:image:width': '1200',
        'og:image:height': '630',
        'og:image:alt': title,
      }),
    },

    // Twitter Card tags
    twitterTags: {
      'twitter:card': 'summary_large_image',
      'twitter:title': title,
      'twitter:description': description,
      ...(tourImage && {
        'twitter:image': tourImage,
        'twitter:image:alt': title,
      }),
    },

    // Facebook tags
    facebookTags: {
      'fb:app_id': fbAppId,
    },

    // Additional tags
    faviconUrl,
  };
}

export function generateStructuredData(metadata: PageMetadata) {
  const { title, description, tourImage, website, slug, type } = metadata;
  const pageUrl = `https://${website}/${type}/${slug}`;

  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url: pageUrl,
    ...(tourImage && {
      image: {
        '@type': 'ImageObject',
        url: tourImage,
        width: 1200,
        height: 630,
      },
    }),
    publisher: {
      '@type': 'Organization',
      name: 'Treedis',
      url: `https://${website}`,
    },
  };
}
